import type { BrandSlug } from './brands'
import { decideAssetBase } from '../asset-origin'
import { BRAND_SLUGS } from './brands'

export type LogoVariant = 'color' | 'white'

export interface LogoSpec {
  alt: string
  // assets/logos/<slug>-<variant>.svg 的 viewBox 寬高（兩個版本相同）。版面只用 height / width。
  width: number
  height: number
}

// 來源：Figma logo 區塊 847:26273（Logo 805:16584 / Logo-inverse 805:16585）。
// logo 是品牌識別（讀者從 alt 得知是哪個 IP 的投票），不是裝飾，所以給有意義的 alt。
export const LOGOS: Record<BrandSlug, LogoSpec> = {
  news: { alt: 'TVBS新聞網', width: 153.485, height: 27.9983 },
  health: { alt: '健康2.0', width: 94, height: 25.7174 },
  cars: { alt: '地球黃金線', width: 128, height: 28 },
  supertaste: { alt: '食尚玩家', width: 66, height: 28 },
  woman: { alt: '女人我最大', width: 69, height: 28 },
}

// 同一個被拒絕的 override 值只 warn 一次：frameView 每次 render 都會呼叫 logoUrl 三次
// （src + 兩個 data-src-*，見 render/frame.ts），沒有這個記憶會讓一次 render 洗出三行一樣的
// warning，而元素每次重渲都會再算一次，被拒的值會一直重複洗版 console。
const warnedRejectedAssetBases = new Set<string>()

// asset base 政策（allowlist、DEV 放寬、非字串 override）在 asset-origin.ts；這支只做一件事：
// 把三個全域讀進來交給 decideAssetBase，再把「被拒絕」這件事說出去（且只說一次）。
function resolveAssetBase(): string {
  const decision = decideAssetBase({
    baked: import.meta.env.VITE_ASSET_BASE_URL,
    // override 來自宿主注入的 global，同 api.ts 的 resolveBaseUrl 不假設其型別。
    override: (globalThis as { Pulse?: { assetBase?: string } }).Pulse?.assetBase,
    isDev: import.meta.env.DEV,
  })
  if (decision.outcome === 'rejected') {
    // 用 String() 而不是把 override 插進樣板，理由同 api.ts 的 resolveBaseUrl：override 是宿主
    // 注入的任意值，樣板對 Symbol 會直接 throw，在這裡 throw 會讓一個「被忽略的設定」升級成
    // 整個 logo 渲染失敗。
    const key = String(decision.override)
    if (!warnedRejectedAssetBases.has(key)) {
      warnedRejectedAssetBases.add(key)
      console.warn(`[Pulse] ignoring assetBase override outside the allowlist: ${key}`)
    }
  }
  return decision.base
}

// 唯一讀 VITE_ASSET_BASE_URL / window.Pulse.assetBase 的地方；每次呼叫才讀
// （測試可 vi.stubEnv / 直接設 globalThis.Pulse）。
export function logoUrl(brand: BrandSlug, variant: LogoVariant): string {
  return `${resolveAssetBase()}/logos/${brand}-${variant}.svg`
}

// .frame[data-brand] → --mp-logo-aspect（高 ÷ 寬）。版面用它算 logo 高與 header／面板高（geometry.ts）。
// 生成字串不放 CSS 註解：strip-css-literal-comments 只處理 css`` 模板。
export function logoAspectCss(): string {
  return BRAND_SLUGS
    .map(b => `.frame[data-brand='${b}'] { --mp-logo-aspect: ${(LOGOS[b].height / LOGOS[b].width).toFixed(6)}; }`)
    .join('\n')
}
